import { Box, Combobox, Flex, Paper, Text, useCombobox } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import type { CustomCellEditorProps } from "ag-grid-react";
import React, { useEffect, useRef, useState } from "react";

type DropdownValue = string | number;
const SEARCH_OPTION_THRESHOLD = 15;

interface DropdownOption {
  value: DropdownValue;
  color?: string | null;
  description?: string | null;
}

interface DropdownEditorProps extends CustomCellEditorProps<any, DropdownValue | DropdownValue[] | null> {
  options: DropdownOption[];
  multiSelect: boolean;
  showDescriptions: boolean;
  formatOption: (value: DropdownValue) => string;
}

export default function OtherAttributeDropdownEditor({
  value,
  onValueChange,
  stopEditing,
  options,
  multiSelect,
  showDescriptions,
  formatOption,
}: DropdownEditorProps) {
  const [search, setSearch] = useState("");
  const combobox = useCombobox({ defaultOpened: true });
  const showSearch = options.length >= SEARCH_OPTION_THRESHOLD;
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSearch) optionsRef.current?.focus();
  }, [showSearch]);

  const selectedValues = Array.isArray(value) ? value : value == null ? [] : [value];
  const isSelected = (optionValue: DropdownValue) =>
    selectedValues.some((selectedValue) => String(selectedValue) === String(optionValue));
  const filteredOptions = options.filter((option) =>
    `${formatOption(option.value)} ${showDescriptions ? option.description ?? "" : ""}`
      .toLocaleLowerCase("nb-NO")
      .includes(search.toLocaleLowerCase("nb-NO")),
  );

  return (
    <Paper withBorder shadow="sm" p={4} w={320} maw="calc(100vw - 24px)">
      <Combobox
        store={combobox}
        onOptionSubmit={(optionValue) => {
          const option = options.find((item) => String(item.value) === optionValue);
          if (option == null) return;

          const selected = isSelected(option.value);
          onValueChange(
            multiSelect
              ? selected
                ? selectedValues.filter((item) => String(item) !== optionValue)
                : [...selectedValues, option.value]
              : selected
              ? null
              : option.value,
          );
          if (!multiSelect) stopEditing();
        }}
      >
        {showSearch && (
          <Combobox.Search
            autoFocus
            aria-label="Søk i alternativer"
            placeholder="Søk"
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              combobox.resetSelectedOption();
            }}
            onKeyDown={(event) => {
              if (["ArrowUp", "ArrowDown", "Enter", "Home", "End"].includes(event.key)) event.stopPropagation();
            }}
          />
        )}
        <Combobox.EventsTarget>
          <Combobox.Options
            ref={optionsRef}
            tabIndex={showSearch ? -1 : 0}
            mah={220}
            style={{ overflowY: "auto" }}
            onKeyDown={(event) => {
              if (["ArrowUp", "ArrowDown", "Enter", "Home", "End"].includes(event.key)) event.stopPropagation();
            }}
          >
            {filteredOptions.map((option) => {
              const selected = isSelected(option.value);
              return (
                <Combobox.Option
                  key={String(option.value)}
                  value={String(option.value)}
                  active={selected}
                  aria-selected={selected}
                >
                  <Flex align="center" gap="xs">
                    {option.color && (
                      <Box w={8} h={8} bg={option.color} style={{ borderRadius: "50%", flexShrink: 0 }} />
                    )}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                        {formatOption(option.value)}
                      </Text>
                      {showDescriptions && option.description && (
                        <Text size="xs" c="dimmed" style={{ overflowWrap: "anywhere" }}>
                          {option.description}
                        </Text>
                      )}
                    </Box>
                    {selected && <IconX size={16} aria-label="Fjern valg" style={{ flexShrink: 0 }} />}
                  </Flex>
                </Combobox.Option>
              );
            })}
            {filteredOptions.length === 0 && <Combobox.Empty>Ingen alternativer</Combobox.Empty>}
          </Combobox.Options>
        </Combobox.EventsTarget>
      </Combobox>
    </Paper>
  );
}
